import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ChatIcon, EnvelopeIcon, LinkIcon } from 'evergreen-ui';
import ModalManager, { useModal } from '@ebay/nice-modal-react';
import { useQueryClient } from 'react-query';
import { CheckMarkIcon } from 'public/icons';
import * as userResource from 'resources/user/hooks';
import { getFullName } from 'resources/user/user.helpers';
import { useCreateDealEvent } from 'resources/deal-event/hooks';
import { DEAL_EVENT_TYPES } from 'resources/deal-event/deal-event.constants';
import copyToClipboard from 'utils/copyToClipboard';
import uiNotifications from 'services/uiNotification.service';
import Modal from 'UI/Modal';
import Button from 'UI/Button';
import Checkbox from 'UI/Checkbox';
import SampleTransactionModal from 'modals/SampleTransactionModal';
import EnterPhoneNumberModal from 'modals/InviteClientModal/EnterPhoneNumberModal';
import EnterEmailModal from 'modals/InviteClientModal/EnterEmailModal';
import styles from './styles.module.scss';


const InviteClientModal = ModalManager.create(({ clients, dealId, isResend }) => {
  const queryClient = useQueryClient();
  const modal = useModal();

  const [isSmsSending, setIsSmsSending] = useState(false);
  const [isEmailSending, setIsEmailSending] = useState(false);
  const [isLinkGenerating, setIsLinkGenerating] = useState(false);
  const [clientsToInvite, setClientsToInvite] = useState([]);

  const { data: user } = userResource.useCurrentUser();
  const { mutateAsync: sendInvite, isLoading: isLoadingSendInvite } = userResource.useSendInvitation();
  const { mutateAsync: resendInvite, isLoading: isLoadingResendInvite } = userResource.useResendInvitation();
  const { mutateAsync: createDealEvent } = useCreateDealEvent();

  const sendOrResendInvite = useMemo(
    () => (isResend ? resendInvite : sendInvite),
    [isResend, resendInvite, sendInvite],
  );

  // -- Email -->
  const onSendEmailInviteClick = async () => {
    const includesSampleClient = clientsToInvite.some((client) => client.app_state.is_sample);
    if (includesSampleClient) {
      ModalManager.show(SampleTransactionModal);
      return;
    }

    const clientsWithoutEmail = clientsToInvite.filter((client) => client.isChecked && !client.email);
    if (clientsWithoutEmail.length) {
      return ModalManager.show(EnterEmailModal, {
        clientsWithoutEmail,
        onSubmit: sendEmailInvite,
      });
    }

    await sendEmailInvite();
  };

  const sendEmailInvite = async () => {
    setIsEmailSending(true);

    try {
      const checkedClientsToInvite = clientsToInvite.filter((client) => client.isChecked);

      await Promise.all(checkedClientsToInvite.map(async (client) => {
        await sendOrResendInvite({ id: client.id, shouldSendEmail: true });

        if (!isResend && dealId) {
          await createDealEvent({
            type: DEAL_EVENT_TYPES.CLIENT_IS_INVITED,
            dealId,
            companyId: user.company_id,
            createdBy: user.id,
            data: { name: getFullName(client) },
          });
        }
      }));

      uiNotifications.showSuccess(`Invite${checkedClientsToInvite.length > 1 ? 's' : ''} sent!`);

      queryClient.invalidateQueries(['users.getTransactionParties', dealId]);
    } finally {
      setIsEmailSending(false);
      modal.hide();
    }
  };
  // -- Email <--

  // -- SMS -->
  const onSendSmsInviteClick = async () => {
    const includesSampleClient = clientsToInvite.some((client) => client.app_state.is_sample);
    if (includesSampleClient) {
      ModalManager.show(SampleTransactionModal);
      return;
    }

    const clientsWithoutNumber = clientsToInvite.filter((client) => client.isChecked && !client.phone_number);
    if (clientsWithoutNumber.length) {
      return ModalManager.show(EnterPhoneNumberModal, {
        clientsWithoutNumber,
        onSubmit: sendSmsInvite,
      });
    }

    await sendSmsInvite();
  };

  const sendSmsInvite = async () => {
    setIsSmsSending(true);

    try {
      const checkedClientsToInvite = clientsToInvite.filter((client) => client.isChecked);

      await Promise.all(checkedClientsToInvite.map(async (client) => {
        await sendOrResendInvite({ id: client.id, shouldSendSms: true });

        if (!isResend && dealId) {
          await createDealEvent({
            type: DEAL_EVENT_TYPES.CLIENT_IS_INVITED,
            dealId,
            companyId: user.company_id,
            createdBy: user.id,
            data: { name: getFullName(client) },
          });
        }
      }));

      uiNotifications.showSuccess(`Invite${clients.length > 1 ? 's' : ''} sent!`);

      queryClient.invalidateQueries(['users.getTransactionParties', dealId]);
    } finally {
      setIsSmsSending(false);
      modal.hide();
    }
  };
  // -- SMS <--

  // -- Link -->
  const onCopyInviteLinkClick = async () => {
    const client = clientsToInvite.find((client) => client.isChecked);
    if (client.app_state.is_sample) {
      ModalManager.show(SampleTransactionModal);
      return;
    }

    setIsLinkGenerating(true);

    const { acceptInviteLink } = await sendOrResendInvite({ id: client.id });
    await copyToClipboard(acceptInviteLink);

    queryClient.invalidateQueries(['users.getTransactionParties', dealId]);
    uiNotifications.showSuccess('Link copied to clipboard');

    setIsLinkGenerating(false);

    modal.hide();
  };
  // -- Link <--

  const toggleClientToInvite = (clientIndex, isChecked) => {
    setClientsToInvite(clientsToInvite.map((client, i) => i === clientIndex
      ? { ...client, isChecked: isChecked }
      : client));
  };

  // init/update clientsToInvite from clients
  useEffect(() => {
    if (clients) {
      setClientsToInvite(clients.map((client) => {
        const existingClientToInvite = clientsToInvite.find((c) => c.id === client.id);
        return {
          isChecked: existingClientToInvite ? existingClientToInvite.isChecked : true,
          ...client,
        };
      }));
    }
  }, [clients]);

  const isInviteDisabled = (
    isLoadingSendInvite ||
    isLoadingResendInvite ||
    clientsToInvite.filter((client) => client.isChecked).length === 0
  );

  const isAnyClientMissingEmail = clientsToInvite.some((client) => !client.email);

  return (
    <Modal
      title="Get the full value of ListedKit by inviting your clients:"
      isOpen={modal.visible}
      onClose={modal.hide}
      onAfterClose={modal.remove}
      className={styles.modal}
    >
      <div>
        <div className={styles.clientsList}>
          {clientsToInvite.map((client, index) => (
            <Checkbox
              key={client.id}
              checked={client.isChecked}
              onChange={() => toggleClientToInvite(index, !client.isChecked)}
              label={getFullName(client)}
              disabled={clientsToInvite.length === 1}
            />
          ))}
        </div>

        <div className={styles.rewardsSection}>
          <p className={styles.rewardsTitle}>
            Up your game and reap the rewards. Invited clients will be able to:
          </p>

          <div className={styles.rewardsList}>
            <p className={styles.rewardsItem}>
              <CheckMarkIcon color="#355B98" />
              View tasks and documents
            </p>
            <p className={styles.rewardsItem}>
              <CheckMarkIcon color="#355B98" />
              Track progress
            </p>
            <p className={styles.rewardsItem}>
              <CheckMarkIcon color="#355B98" />
              Refer more business
            </p>
          </div>
        </div>

        <div className={styles.buttonsContainer}>
          <Button
            iconBefore={<EnvelopeIcon size={12} />}
            type="default"
            onClick={onSendEmailInviteClick}
            disabled={isInviteDisabled}
            loading={isEmailSending}
          >
            Send Email Invite
          </Button>

          {!isAnyClientMissingEmail && (
            <Button
              iconBefore={<ChatIcon size={12} />}
              type="default"
              onClick={onSendSmsInviteClick}
              loading={isSmsSending}
              disabled={isInviteDisabled}
            >
              Send SMS Invite
            </Button>
          )}

          {clientsToInvite.length === 1 && clientsToInvite[0].email && (
            <Button
              iconBefore={<LinkIcon size={12} />}
              type="default"
              onClick={onCopyInviteLinkClick}
              disabled={isInviteDisabled}
              loading={isLinkGenerating}
            >
              Copy Link
            </Button>
          )}

          <Button type="minimal" style={{ fontWeight: 400 }} onClick={modal.hide}>
            Not Now
          </Button>
        </div>
      </div>
    </Modal>
  );
});

InviteClientModal.propTypes = {
  clients: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    first_name: PropTypes.string.isRequired,
    last_name: PropTypes.string.isRequired,
  })).isRequired,
  dealId: PropTypes.string,
  isResend: PropTypes.bool,
};

InviteClientModal.defaultProps = {
  clients: [],
  dealId: null,
  isResend: false,
};

export default React.memo(InviteClientModal);
